import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
// Import Shared Components
import {
  Modal,
  ActionBar,
  ButtonGroup,
  Loader,
  Input,
} from 'shared-components';
// Import Shared Utils
import { post } from 'shared-components/src/utils/http';
// Import Global Components
import Heading from '../../../../components/Heading';
import Alert from '../../../../components/Alert';
import CancelButton from '../../../../components/CancelButton';
import SubmitButton from '../../../../components/SubmitButton';
// Import Global Layouts
import Form from '../../../../layouts/Form';
import InputRow from '../../../../layouts/InputRow';
import InputGroup from '../../../../layouts/InputGroup';
// Import Translations
import { profileEn } from '../../../../i18n/profile';
// Import Utils
import {
  PIN_LENGTH,
  toPinEntry,
  pinRuleViolation,
} from '../../../../utils/pinRules';
// Import Local Layouts
import PinModal from '../../layouts/PinModal';
// Import Component Styles
import { Requirements } from './styles';

const SetPinModal = ({
  toggleModal,
  currentAccount,
  pin,
  setPin,
  alert,
  setAlert,
  onPinChanged,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  // Empty, not seeded with the current PIN: prefilling would defeat the confirmation
  // field below.
  const [pinInput, setPinInput] = useState('');
  const [confirmInput, setConfirmInput] = useState('');

  const page = 'profile';

  const updatePin = async params => {
    setLoading(true);
    try {
      await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${currentAccount.id}/set_pin`,
        params
      );

      setPin(pinInput);
      // The modal closes on success, so the confirmation goes to the card banner
      // outside it. Set on the modal's own alert it would only show on the next open.
      setAlert(null);
      onPinChanged({
        message: profileEn[`${page}-pin-changed-alert`],
        type: 'success',
        messageFor: 'pin-changed',
      });

      toggleModal();
    } catch (e) {
      // The processor's reason does not survive the trip back (it arrives as a bare
      // "Failed to send request."), and a refused PIN is the likely cause, so say so
      // in the cardholder's language rather than echo it.
      setAlert({
        message: profileEn[`${page}-pin-rejected-alert`],
        type: 'danger',
        messageFor: 'pin-rejected',
      });
    }
    setLoading(false);
  };

  // What is wrong with one field, as an alert id, or null. Checked here rather
  // than with the browser's pattern check, whose tooltip follows the browser's
  // language, not the portal's.
  const fieldIssue = field => {
    if (field === 'pin') {
      if (pinInput.length !== PIN_LENGTH) return 'pin-invalid';
      const violation = pinRuleViolation(
        pinInput,
        currentAccount.cards[0].last_four_digits
      );
      if (violation) return `pin-${violation}`;
      // pin is only known if the cardholder viewed one earlier in the session, so
      // this check is best-effort — the processor is the authority on a reuse.
      if (pin && pin === pinInput) return 'different-pin';
      return null;
    }
    if (confirmInput.length !== PIN_LENGTH) return 'pin-invalid';
    // Nothing to match against until the new PIN is whole; its own blur says so.
    if (pinInput.length === PIN_LENGTH && pinInput !== confirmInput)
      return 'pin-mismatch';
    return null;
  };

  // `field` records which input raised the alert, so fixing that input clears it
  // without hiding a problem the other one still has.
  const showIssue = (field, issue) =>
    setAlert({
      message: profileEn[`${page}-${issue}-alert`],
      type: 'danger',
      messageFor: issue,
      field,
    });

  const handleBlur = field => {
    const issue = fieldIssue(field);
    if (issue) showIssue(field, issue);
    else if (alert && alert.field === field) setAlert(null);
  };

  const handleSubmit = e => {
    e.preventDefault();

    const field = ['pin', 'confirm'].find(f => fieldIssue(f));
    if (field) return showIssue(field, fieldIssue(field));

    const params = {
      last_four_digits: currentAccount.cards[0].last_four_digits,
      pin: pinInput,
    };

    return updatePin(params);
  };

  return (
    <Modal>
      <PinModal>
        <Heading
          page={`${page}-set-pin`}
          heading={profileEn[`${page}-set-pin-heading`]}
        />
        <Requirements
          text={profileEn[`${page}-pin-requirements-text`]}
          textFor="pin-requirements"
          page={page}
        />
        {loading ? (
          <Loader />
        ) : (
          <Form onSubmit={handleSubmit}>
            <InputRow>
              <InputGroup
                label={profileEn[`${page}-new-pin-label`]}
                labelFor="new-pin"
                page={page}
              >
                <Input
                  value={pinInput}
                  onChange={e => setPinInput(toPinEntry(e.target.value))}
                  onBlur={() => handleBlur('pin')}
                  data-testid="pin"
                  name="pin"
                  maxLength={PIN_LENGTH}
                  inputMode="numeric"
                  placeholder={intl.messages[`${page}-new-pin-placeholder`]}
                />
              </InputGroup>
            </InputRow>
            <InputRow>
              <InputGroup
                label={profileEn[`${page}-confirm-pin-label`]}
                labelFor="confirm-pin"
                page={page}
              >
                <Input
                  value={confirmInput}
                  onChange={e => setConfirmInput(toPinEntry(e.target.value))}
                  onBlur={() => handleBlur('confirm')}
                  data-testid="confirm-pin"
                  name="confirm_pin"
                  maxLength={PIN_LENGTH}
                  inputMode="numeric"
                  placeholder={intl.messages[`${page}-confirm-pin-placeholder`]}
                />
              </InputGroup>
            </InputRow>
            {/* Beside the button that raised it, rather than above the heading,
                out of view of someone looking at Submit. */}
            {alert && (
              <Alert
                alert={alert.message}
                alertFor={alert.messageFor}
                page={page}
                type={alert.type}
              />
            )}
            <ActionBar>
              <ButtonGroup reverse>
                <SubmitButton />
                <CancelButton
                  onClick={e => {
                    e.preventDefault();
                    toggleModal(false);
                  }}
                />
              </ButtonGroup>
            </ActionBar>
          </Form>
        )}
      </PinModal>
    </Modal>
  );
};

SetPinModal.defaultProps = {
  pin: null,
  onPinChanged: () => {},
};

SetPinModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  // Null when the modal is opened directly rather than from the View PIN screen.
  pin: PropTypes.string,
  setPin: PropTypes.func.isRequired,
  currentAccount: PropTypes.shape({
    id: PropTypes.number.isRequired,
    cards: PropTypes.array.isRequired, //eslint-disable-line
  }).isRequired,
  alert: PropTypes.shape({
    message: PropTypes.string.isRequired,
    messageFor: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    // Which input raised a validation alert ('pin' | 'confirm'); absent otherwise.
    field: PropTypes.string,
  }).isRequired,
  setAlert: PropTypes.func.isRequired,
  // Receives the success alert, for display outside the modal once it closes.
  onPinChanged: PropTypes.func,
};

export default SetPinModal;
